import React from 'react';
import { useSpring } from 'react-spring';
import useMeasure from 'react-use-measure';

import chevronDown from '../../assets/icons/chevron-down.svg';
import chevronUp from '../../assets/icons/chevron-up.svg';

import { findByType } from '../../utils';

import {
  Container,
  HeaderContainer,
  HeaderSeparator,
  HiddenContainer,
  ShownContainer,
} from './Expandable.styles';
import { ExpandableProps, ExpandableSubComponents } from './Expandable.types';

const Shown: React.FC = () => null;
Shown.displayName = 'Shown';

const Hidden: React.FC = () => null;
Hidden.displayName = 'Hidden';

const Expandable: React.FC<ExpandableProps> & ExpandableSubComponents = ({ ...props }) => {
  const { expanded, changeExpanded, headerContent, children } = props;

  const [ref, bounds] = useMeasure();

  const panelContentAnimatedStyle = useSpring({
    height: expanded ? bounds.height : 0,
  });

  const renderShown = () => {
    const shown = findByType(children, Shown);
    if (!shown) {
      return null;
    }
    return <ShownContainer data-testid="expandable-shown">{shown.props.children}</ShownContainer>;
  };

  const renderHidden = () => {
    const hidden = findByType(children, Hidden);
    if (!hidden) {
      return null;
    }
    return (
      <HiddenContainer data-testid="expandable-hidden" style={panelContentAnimatedStyle}>
        {!findByType(children, Shown) && <HeaderSeparator />}
        <div ref={ref}>{hidden.props.children}</div>
      </HiddenContainer>
    );
  };

  return (
    <Container {...props}>
      <HeaderContainer>
        {headerContent}
        <div style={{ width: '30px' }}></div>
        {expanded ? (
          <img
            data-testid="collapse-button"
            src={chevronUp}
            alt="COL"
            onClick={() => changeExpanded(false)}
          />
        ) : (
          <img
            data-testid="expand-button"
            src={chevronDown}
            alt="EXP"
            onClick={() => changeExpanded(true)}
          />
        )}
      </HeaderContainer>
      {findByType(children, Shown) && <HeaderSeparator />}
      {renderShown()}
      {renderHidden()}
    </Container>
  );
};

Expandable.Shown = Shown;
Expandable.Hidden = Hidden;

export { Expandable };
